<template>
  <div>
    <v-container fluid>
      <!-- ============================================================ -->
      <!-- ส่วนที่ 1: หัวข้อหลัก + แจ้งเตือน                           -->
      <!-- ============================================================ -->
      <base-material-card
        icon="mdi-file-document-check"
        title="รายงานรอบข้อมูลบุคลากรสถานศึกษา"
        class="px-5 py-3"
      >
        <!-- แจ้งเตือนรอบที่เปิดอยู่ -->
        <v-card
          v-if="activeSubmission && activeSubmission.round_name"
          class="mb-4"
          flat
        >
          <v-alert
            :type="alertType"
            prominent
            outlined
            border="left"
            class="mb-0"
          >
            <v-row align="center" no-gutters>
              <v-col cols="12" md="8">
                <div class="text-h6 font-weight-bold">
                  <v-icon left :color="alertColor">mdi-calendar-clock</v-icon>
                  {{ activeSubmission.round_name }}
                </div>
                <div class="mt-1">
                  ปีงบประมาณ {{ activeSubmission.cycle_fiscal_year }} |
                  ข้อมูลช่วง
                  {{
                    formatThaiDate(activeSubmission.round_data_period_start)
                  }}
                  ถึง
                  {{ formatThaiDate(activeSubmission.round_data_period_end) }}
                </div>
                <div class="mt-1">
                  <v-chip small :color="daysRemainingColor" dark class="mr-2">
                    <v-icon small left>mdi-timer-sand</v-icon>
                    เหลือ {{ activeSubmission.days_remaining }} วัน
                  </v-chip>
                  <v-chip
                    small
                    :color="statusColor(activeSubmission.submission_status)"
                    dark
                  >
                    <v-icon small left>{{
                      statusIcon(activeSubmission.submission_status)
                    }}</v-icon>
                    {{ statusLabel(activeSubmission.submission_status) }}
                  </v-chip>
                </div>
                <!-- แจ้งเตือนชั่วโมงสอนตามรอบ -->
                <div class="mt-2" v-if="activeRoundTeachSemester">
                  <v-chip small color="orange darken-2" dark>
                    <v-icon small left>mdi-clock-outline</v-icon>
                    รอบนี้รายงาน: {{ activeRoundTeachSemesterLabel }}
                  </v-chip>
                </div>
              </v-col>
              <v-col cols="12" md="4" class="text-right">
                <div class="text-caption grey--text">กำหนดปิดรอบ</div>
                <div
                  class="text-h5 font-weight-bold"
                  :class="daysRemainingColor + '--text'"
                >
                  {{ formatThaiDate(activeSubmission.round_end_date) }}
                </div>
              </v-col>
            </v-row>
          </v-alert>
        </v-card>

        <!-- ไม่มีรอบเปิดอยู่ -->
        <v-card v-else-if="!loading" class="mb-4" flat>
          <v-alert
            :type="
              activeSubmission && activeSubmission.status === false
                ? 'warning'
                : 'info'
            "
            outlined
            prominent
            border="left"
          >
            <v-icon
              left
              :color="
                activeSubmission && activeSubmission.status === false
                  ? 'warning'
                  : 'info'
              "
            >
              {{
                activeSubmission && activeSubmission.status === false
                  ? "mdi-alert"
                  : "mdi-information"
              }}
            </v-icon>
            {{
              activeSubmission && activeSubmission.message
                ? activeSubmission.message
                : "ขณะนี้ยังไม่มีรอบรายงานที่เปิดอยู่ กรุณารอ Admin เปิดรอบรายงาน"
            }}
            <div
              v-if="activeSubmission && activeSubmission.status === false"
              class="mt-2 text-caption"
            >
              รหัสสถานศึกษาที่ใช้ค้นหา: <strong>{{ collegeCode }}</strong>
            </div>
          </v-alert>
        </v-card>

        <!-- ============================================================ -->
        <!-- ส่วนที่ 2: Stepper แสดงขั้นตอน                              -->
        <!-- ============================================================ -->
        <v-card
          v-if="activeSubmission && activeSubmission.submission_id"
          class="mb-4 pa-4"
          outlined
        >
          <div class="text-subtitle-1 font-weight-bold mb-3">
            <v-icon left color="primary">mdi-progress-check</v-icon>
            ขั้นตอนการรายงานข้อมูล
          </div>
          <v-stepper v-model="currentStep" alt-labels flat class="elevation-0">
            <v-stepper-header>
              <v-stepper-step
                :complete="isStepComplete(1)"
                step="1"
                :color="stepColor(1)"
              >
                ตรวจสอบข้อมูล
                <small>ปรับปรุงให้เป็นปัจจุบัน</small>
              </v-stepper-step>
              <v-divider></v-divider>
              <v-stepper-step
                :complete="isStepComplete(2)"
                step="2"
                :color="stepColor(2)"
              >
                ยืนยันข้อมูล
                <small>สร้าง Snapshot</small>
              </v-stepper-step>
              <v-divider></v-divider>
              <v-stepper-step
                :complete="isStepComplete(3)"
                step="3"
                :color="stepColor(3)"
              >
                สร้างรายงาน PDF
                <small>ส่งออก mPDF</small>
              </v-stepper-step>
              <v-divider></v-divider>
              <v-stepper-step
                :complete="isStepComplete(4)"
                step="4"
                :color="stepColor(4)"
              >
                ผอ. รับรอง
                <small>ลงนามรับรอง</small>
              </v-stepper-step>
              <v-divider></v-divider>
              <v-stepper-step
                :complete="isStepComplete(5)"
                step="5"
                :color="stepColor(5)"
              >
                อัปโหลดเอกสาร
                <small>แนบ PDF ลงนาม</small>
              </v-stepper-step>
              <v-divider></v-divider>
              <v-stepper-step
                :complete="isStepComplete(6)"
                step="6"
                :color="stepColor(6)"
              >
                เสร็จสิ้น
                <small>รายงานครบถ้วน</small>
              </v-stepper-step>
            </v-stepper-header>
          </v-stepper>
        </v-card>

        <!-- ============================================================ -->
        <!-- ส่วนที่ 3: สรุปจำนวนบุคลากรปัจจุบัน                        -->
        <!-- ============================================================ -->
        <v-card class="mb-4 pa-4" outlined>
          <div class="text-subtitle-1 font-weight-bold mb-3">
            <v-icon left color="teal">mdi-account-group</v-icon>
            สรุปจำนวนบุคลากรปัจจุบัน —
            {{
              activeSubmission.college_name ||
                user.college_name ||
                user.user_firstname ||
                ""
            }}
          </div>

          <v-row>
            <v-col
              v-for="(item, idx) in personnelSummaryCards"
              :key="idx"
              cols="6"
              sm="4"
              md="3"
              lg="2"
            >
              <v-card
                :color="item.bg"
                class="pa-3 text-center"
                flat
                rounded="lg"
              >
                <div class="text-h5 font-weight-bold" :class="item.textColor">
                  {{ item.count }}
                </div>
                <div
                  class="text-caption"
                  :class="item.textColor"
                  style="line-height: 1.2;"
                >
                  {{ item.label }}
                </div>
              </v-card>
            </v-col>
            <v-col cols="6" sm="4" md="3" lg="2">
              <v-card
                color="blue-grey darken-3"
                class="pa-3 text-center"
                dark
                flat
                rounded="lg"
              >
                <div class="text-h5 font-weight-bold">
                  {{ personnel_etcsCounts.sumAll || 0 }}
                </div>
                <div class="text-caption" style="line-height: 1.2;">
                  รวมทั้งสิ้น
                </div>
              </v-card>
            </v-col>
          </v-row>
        </v-card>

        <!-- ============================================================ -->
        <!-- ส่วนที่ 3.5: แจ้งเตือนชั่วโมงสอน (ใหม่)                    -->
        <!-- ============================================================ -->
        <v-card
          v-if="
            activeRoundTeachSemester &&
              activeSubmission &&
              activeSubmission.submission_id
          "
          class="mb-4 pa-3"
          outlined
          color="orange lighten-5"
        >
          <v-card-title class="pb-1">
            <v-icon left color="orange darken-2"
              >mdi-clock-alert-outline</v-icon
            >
            <span class="font-weight-bold orange--text text--darken-3">
              สถานะชั่วโมงสอน — {{ activeRoundTeachSemesterLabel }}
            </span>
          </v-card-title>
          <v-card-text>
            <v-row>
              <v-col cols="12" md="4">
                <v-card outlined class="pa-3 text-center">
                  <v-icon color="green" size="28">mdi-school</v-icon>
                  <div class="text-h5 font-weight-bold mt-1">
                    {{ teachHoursStats.totalTeachers }}
                  </div>
                  <div class="caption">สายการสอนทั้งหมด</div>
                </v-card>
              </v-col>
              <v-col cols="12" md="4">
                <v-card outlined class="pa-3 text-center">
                  <v-icon color="green" size="28">mdi-check-circle</v-icon>
                  <div class="text-h5 font-weight-bold mt-1 green--text">
                    {{ teachHoursStats.withHours }}
                  </div>
                  <div class="caption">กรอกชั่วโมงสอนแล้ว</div>
                </v-card>
              </v-col>
              <v-col cols="12" md="4">
                <v-card
                  outlined
                  class="pa-3 text-center"
                  :color="
                    teachHoursStats.withoutHours > 0 ? 'red lighten-5' : ''
                  "
                >
                  <v-icon
                    :color="teachHoursStats.withoutHours > 0 ? 'red' : 'green'"
                    size="28"
                  >
                    {{
                      teachHoursStats.withoutHours > 0
                        ? "mdi-alert-circle"
                        : "mdi-check-all"
                    }}
                  </v-icon>
                  <div
                    class="text-h5 font-weight-bold mt-1"
                    :class="
                      teachHoursStats.withoutHours > 0
                        ? 'red--text'
                        : 'green--text'
                    "
                  >
                    {{ teachHoursStats.withoutHours }}
                  </div>
                  <div class="caption">ยังไม่กรอกชั่วโมงสอน</div>
                </v-card>
              </v-col>
            </v-row>
            <v-alert
              v-if="teachHoursStats.withoutHours > 0"
              type="warning"
              dense
              text
              class="mt-3 mb-0"
            >
              <v-icon left small>mdi-information</v-icon>
              กรุณาตรวจสอบข้อมูลชั่วโมงสอน{{
                activeRoundTeachSemesterLabel
              }}ให้ครบถ้วนก่อนยืนยันข้อมูล — ไปหน้า
              <router-link to="/college/personnel_etc" class="font-weight-bold"
                >จัดการข้อมูลบุคลากร</router-link
              >
            </v-alert>
          </v-card-text>
        </v-card>

        <!-- ============================================================ -->
        <!-- ส่วนที่ 4: Action Panel — ปุ่มดำเนินการตามขั้นตอน           -->
        <!-- ============================================================ -->
        <v-card
          v-if="activeSubmission && activeSubmission.submission_id"
          class="mb-4"
          outlined
        >
          <v-card-title class="text-subtitle-1 font-weight-bold pb-2">
            <v-icon left color="orange darken-2">mdi-cog-play</v-icon>
            ดำเนินการรายงานรอบปัจจุบัน
          </v-card-title>
          <v-divider></v-divider>
          <v-card-text>
            <!-- แถบยกเลิก/เริ่มใหม่ -->
            <v-alert
              v-if="canReset"
              type="warning"
              outlined
              dense
              border="left"
              class="mb-4"
            >
              <v-row align="center" no-gutters>
                <v-col cols="12" md="8">
                  <div class="font-weight-bold">
                    <v-icon left small color="warning"
                      >mdi-restart-alert</v-icon
                    >
                    ต้องการแก้ไขข้อมูลใหม่?
                  </div>
                  <div class="text-body-2 mt-1">
                    หากพบข้อมูลไม่ถูกต้อง
                    สามารถยกเลิกการยืนยันและเริ่มรายงานใหม่ได้
                  </div>
                </v-col>
                <v-col cols="12" md="4" class="text-right">
                  <v-btn
                    color="warning"
                    dark
                    depressed
                    rounded
                    :loading="resetLoading"
                    @click="resetSubmission()"
                  >
                    <v-icon left small>mdi-restart</v-icon>
                    ยกเลิก / เริ่มรายงานใหม่
                  </v-btn>
                </v-col>
              </v-row>
            </v-alert>

            <v-row>
              <!-- Step 1: ตรวจสอบข้อมูล -->
              <v-col cols="12" md="6" lg="4">
                <v-card
                  outlined
                  :class="{ 'border-active': currentStep === 1 }"
                  class="step-card pa-4"
                >
                  <div class="d-flex align-center mb-2">
                    <v-avatar size="36" color="blue lighten-4" class="mr-3">
                      <v-icon color="blue darken-2" small
                        >mdi-clipboard-edit</v-icon
                      >
                    </v-avatar>
                    <div>
                      <div class="text-subtitle-2 font-weight-bold">
                        ขั้นตอนที่ 1
                      </div>
                      <div class="text-caption grey--text">
                        ตรวจสอบและปรับปรุงข้อมูล
                      </div>
                    </div>
                  </div>
                  <div class="text-body-2 grey--text text--darken-1 mb-3">
                    ตรวจสอบรายชื่อบุคลากรให้เป็นปัจจุบัน
                    <span
                      v-if="activeRoundTeachSemester"
                      class="orange--text text--darken-3 font-weight-bold"
                    >
                      รวมถึงกรอก{{ activeRoundTeachSemesterLabel }}ให้ครบถ้วน
                    </span>
                  </div>
                  <v-btn
                    color="primary"
                    depressed
                    block
                    :to="'/college/personnel_etc'"
                  >
                    <v-icon left small>mdi-account-edit</v-icon>
                    ไปหน้าจัดการข้อมูลบุคลากร
                  </v-btn>
                </v-card>
              </v-col>

              <!-- Step 2: ยืนยันข้อมูล -->
              <v-col cols="12" md="6" lg="4">
                <v-card
                  outlined
                  :class="{ 'border-active': currentStep === 2 }"
                  class="step-card pa-4"
                >
                  <div class="d-flex align-center mb-2">
                    <v-avatar size="36" color="teal lighten-4" class="mr-3">
                      <v-icon color="teal darken-2" small
                        >mdi-check-decagram</v-icon
                      >
                    </v-avatar>
                    <div>
                      <div class="text-subtitle-2 font-weight-bold">
                        ขั้นตอนที่ 2
                      </div>
                      <div class="text-caption grey--text">
                        ยืนยันความถูกต้องของข้อมูล
                      </div>
                    </div>
                  </div>
                  <div class="text-body-2 grey--text text--darken-1 mb-3">
                    เมื่อตรวจสอบข้อมูลเรียบร้อยแล้ว กดยืนยันเพื่อให้ระบบ สร้าง
                    Snapshot ข้อมูลบุคลากร ณ เวลานี้
                    <span
                      v-if="activeRoundTeachSemester"
                      class="orange--text text--darken-3"
                    >
                      (รวมชั่วโมงสอน{{ activeRoundTeachSemesterLabel }})
                    </span>
                  </div>
                  <v-btn
                    :color="canConfirm ? 'teal' : 'grey'"
                    :disabled="!canConfirm"
                    :loading="confirmLoading"
                    depressed
                    block
                    dark
                    @click="confirmData()"
                  >
                    <v-icon left small>mdi-database-check</v-icon>
                    ยืนยันข้อมูลและสร้าง Snapshot
                  </v-btn>
                  <div
                    v-if="activeSubmission.submission_confirmed_at"
                    class="text-caption success--text mt-2 text-center"
                  >
                    <v-icon x-small color="success">mdi-check-circle</v-icon>
                    ยืนยันเมื่อ
                    {{
                      formatThaiDateTime(
                        activeSubmission.submission_confirmed_at
                      )
                    }}
                  </div>
                </v-card>
              </v-col>

              <!-- Step 3: สร้าง PDF -->
              <v-col cols="12" md="6" lg="4">
                <v-card
                  outlined
                  :class="{ 'border-active': currentStep === 3 }"
                  class="step-card pa-4"
                >
                  <div class="d-flex align-center mb-2">
                    <v-avatar
                      size="36"
                      color="deep-purple lighten-4"
                      class="mr-3"
                    >
                      <v-icon color="deep-purple darken-2" small
                        >mdi-file-pdf-box</v-icon
                      >
                    </v-avatar>
                    <div>
                      <div class="text-subtitle-2 font-weight-bold">
                        ขั้นตอนที่ 3
                      </div>
                      <div class="text-caption grey--text">สร้างรายงาน PDF</div>
                    </div>
                  </div>
                  <div class="text-body-2 grey--text text--darken-1 mb-3">
                    สร้างรายงาน PDF จากข้อมูลที่ยืนยันแล้ว
                    <span
                      v-if="activeRoundTeachSemester"
                      class="orange--text text--darken-3"
                    >
                      (รวมคอลัมน์ชั่วโมงสอน ภ.{{ activeRoundTeachSemester }})
                    </span>
                  </div>
                  <v-btn
                    :color="canGeneratePDF ? 'deep-purple' : 'grey'"
                    :disabled="!canGeneratePDF"
                    :loading="pdfLoading"
                    depressed
                    block
                    dark
                    @click="generatePDF()"
                  >
                    <v-icon left small>mdi-printer</v-icon>
                    สร้างรายงาน PDF
                  </v-btn>
                  <v-btn
                    v-if="generatedPDFPath"
                    color="deep-purple lighten-4"
                    text
                    block
                    small
                    class="mt-2"
                    @click="
                      openPdfPreview(generatedPDFPath, 'รายงานข้อมูลบุคลากร')
                    "
                  >
                    <v-icon left small>mdi-eye</v-icon>
                    ดูรายงาน PDF
                  </v-btn>
                </v-card>
              </v-col>

              <!-- Step 4: ผอ. รับรอง -->
              <v-col cols="12" md="6" lg="4">
                <v-card
                  outlined
                  :class="{ 'border-active': currentStep === 4 }"
                  class="step-card pa-4"
                >
                  <div class="d-flex align-center mb-2">
                    <v-avatar size="36" color="amber lighten-4" class="mr-3">
                      <v-icon color="amber darken-3" small>mdi-seal</v-icon>
                    </v-avatar>
                    <div>
                      <div class="text-subtitle-2 font-weight-bold">
                        ขั้นตอนที่ 4
                      </div>
                      <div class="text-caption grey--text">
                        ผู้อำนวยการรับรอง
                      </div>
                    </div>
                  </div>
                  <div class="text-body-2 grey--text text--darken-1 mb-3">
                    นำรายงาน PDF ให้ผู้อำนวยการตรวจสอบและลงนามรับรอง
                  </div>
                  <v-btn
                    :color="canCertify ? 'amber darken-2' : 'grey'"
                    :disabled="!canCertify"
                    :loading="certifyLoading"
                    depressed
                    block
                    dark
                    @click="certifyData()"
                  >
                    <v-icon left small>mdi-draw-pen</v-icon>
                    ยืนยันว่า ผอ. รับรองแล้ว
                  </v-btn>
                  <div
                    v-if="activeSubmission.submission_certified_at"
                    class="text-caption success--text mt-2 text-center"
                  >
                    <v-icon x-small color="success">mdi-check-circle</v-icon>
                    รับรองเมื่อ
                    {{
                      formatThaiDateTime(
                        activeSubmission.submission_certified_at
                      )
                    }}
                  </div>
                </v-card>
              </v-col>

              <!-- Step 5: อัปโหลดเอกสารลงนาม -->
              <v-col cols="12" md="6" lg="4">
                <v-card
                  outlined
                  :class="{ 'border-active': currentStep === 5 }"
                  class="step-card pa-4"
                >
                  <div class="d-flex align-center mb-2">
                    <v-avatar size="36" color="red lighten-4" class="mr-3">
                      <v-icon color="red darken-2" small
                        >mdi-cloud-upload</v-icon
                      >
                    </v-avatar>
                    <div>
                      <div class="text-subtitle-2 font-weight-bold">
                        ขั้นตอนที่ 5
                      </div>
                      <div class="text-caption grey--text">
                        อัปโหลดเอกสารลงนาม
                      </div>
                    </div>
                  </div>
                  <div class="text-body-2 grey--text text--darken-1 mb-3">
                    Scan หรือถ่ายภาพเอกสารที่ผู้อำนวยการลงนามแล้ว อัปโหลดไฟล์
                    PDF
                  </div>
                  <v-file-input
                    v-if="canUpload"
                    v-model="signedPDFFile"
                    accept="application/pdf"
                    label="เลือกไฟล์ PDF ลงนาม"
                    prepend-icon="mdi-paperclip"
                    outlined
                    dense
                    :rules="[
                      v =>
                        !v || v.size <= 10485760 || 'ขนาดไฟล์ต้องไม่เกิน 10MB'
                    ]"
                    class="mb-2"
                  ></v-file-input>
                  <v-btn
                    :color="
                      canUpload && signedPDFFile ? 'red darken-1' : 'grey'
                    "
                    :disabled="!canUpload || !signedPDFFile"
                    :loading="uploadLoading"
                    depressed
                    block
                    dark
                    @click="uploadSignedPDF()"
                  >
                    <v-icon left small>mdi-upload</v-icon>
                    อัปโหลดเอกสารลงนาม
                  </v-btn>
                  <div
                    v-if="activeSubmission.submission_uploaded_at"
                    class="text-caption success--text mt-2 text-center"
                  >
                    <v-icon x-small color="success">mdi-check-circle</v-icon>
                    อัปโหลดเมื่อ
                    {{
                      formatThaiDateTime(
                        activeSubmission.submission_uploaded_at
                      )
                    }}
                  </div>
                </v-card>
              </v-col>

              <!-- Step 6: เสร็จสิ้น -->
              <v-col cols="12" md="6" lg="4">
                <v-card
                  outlined
                  :class="{
                    'border-active': currentStep === 6,
                    'border-success': isComplete
                  }"
                  class="step-card pa-4"
                >
                  <div class="d-flex align-center mb-2">
                    <v-avatar
                      size="36"
                      :color="isComplete ? 'green lighten-4' : 'grey lighten-3'"
                      class="mr-3"
                    >
                      <v-icon
                        :color="isComplete ? 'green darken-2' : 'grey'"
                        small
                        >mdi-flag-checkered</v-icon
                      >
                    </v-avatar>
                    <div>
                      <div class="text-subtitle-2 font-weight-bold">
                        ขั้นตอนที่ 6
                      </div>
                      <div class="text-caption grey--text">รายงานเสร็จสิ้น</div>
                    </div>
                  </div>
                  <div class="text-body-2 grey--text text--darken-1 mb-3">
                    <template v-if="isComplete"
                      >การรายงานรอบนี้เสร็จสมบูรณ์แล้ว</template
                    >
                    <template v-else
                      >กรุณาดำเนินการตามขั้นตอนข้างต้นให้ครบถ้วน</template
                    >
                  </div>
                  <v-alert
                    v-if="isComplete"
                    type="success"
                    dense
                    text
                    class="mb-0"
                  >
                    <v-icon left small>mdi-check-all</v-icon>
                    รายงานรอบนี้เสร็จสมบูรณ์
                  </v-alert>
                  <v-progress-linear
                    v-else
                    :value="completionPercent"
                    color="primary"
                    height="8"
                    rounded
                    class="mt-2"
                  ></v-progress-linear>
                  <div
                    v-if="!isComplete"
                    class="text-caption text-center mt-1 grey--text"
                  >
                    ดำเนินการแล้ว {{ completionPercent }}%
                  </div>
                </v-card>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <!-- ============================================================ -->
        <!-- ส่วนที่ 5: ข้อมูล Snapshot (ดูได้หลังยืนยัน)               -->
        <!-- ============================================================ -->
        <v-card v-if="snapshotData.length > 0" class="mb-4" outlined>
          <v-card-title class="text-subtitle-1 font-weight-bold pb-2">
            <v-icon left color="indigo">mdi-database-eye</v-icon>
            ข้อมูลบุคลากร Snapshot — ณ เวลายืนยัน
            <v-chip
              v-if="activeRoundTeachSemester"
              small
              color="orange darken-2"
              dark
              class="ml-3"
            >
              <v-icon small left>mdi-clock-outline</v-icon>
              รวม{{ activeRoundTeachSemesterLabel }}
            </v-chip>
            <v-spacer></v-spacer>
            <v-text-field
              v-model="snapshotSearch"
              append-icon="mdi-magnify"
              label="ค้นหา"
              single-line
              hide-details
              dense
              outlined
              style="max-width: 300px;"
            ></v-text-field>
          </v-card-title>
          <v-divider></v-divider>
          <v-data-table
            :headers="computedSnapshotHeaders"
            :items="snapshotData"
            :search="snapshotSearch"
            :items-per-page="15"
            :footer-props="{ 'items-per-page-options': [10, 15, 30, 50, -1] }"
            dense
            class="elevation-0"
          >
            <template v-slot:item.snapshot_m_type="{ item }">
              <v-chip x-small :color="typeColor(item.snapshot_m_type)" dark>
                {{ typeLabel(item.snapshot_m_type) }}
              </v-chip>
            </template>
            <template v-slot:item.fullname="{ item }">
              {{ item.snapshot_title }}{{ item.snapshot_fristname }}
              {{ item.snapshot_lastname }}
            </template>
            <template v-slot:item.snapshot_salary="{ item }">
              {{
                item.snapshot_salary
                  ? Number(item.snapshot_salary).toLocaleString()
                  : "-"
              }}
            </template>
            <!-- ชั่วโมงสอน ภ.1 -->
            <template v-slot:item.snapshot_teach_hours_s1="{ item }">
              <template v-if="isTeachingType(item.snapshot_m_type)">
                <v-chip
                  x-small
                  v-if="item.snapshot_teach_hours_s1"
                  :color="
                    parseInt(item.snapshot_teach_hours_s1) >= 18
                      ? 'success'
                      : 'warning'
                  "
                  dark
                  >{{ item.snapshot_teach_hours_s1 }} ชม.</v-chip
                >
                <span v-else class="grey--text">-</span>
              </template>
              <span v-else>-</span>
            </template>
            <!-- ชั่วโมงสอน ภ.2 -->
            <template v-slot:item.snapshot_teach_hours_s2="{ item }">
              <template v-if="isTeachingType(item.snapshot_m_type)">
                <v-chip
                  x-small
                  v-if="item.snapshot_teach_hours_s2"
                  :color="
                    parseInt(item.snapshot_teach_hours_s2) >= 18
                      ? 'success'
                      : 'warning'
                  "
                  dark
                  >{{ item.snapshot_teach_hours_s2 }} ชม.</v-chip
                >
                <span v-else class="grey--text">-</span>
              </template>
              <span v-else>-</span>
            </template>
          </v-data-table>
        </v-card>

        <!-- ============================================================ -->
        <!-- ส่วนที่ 6: ประวัติรายงานรอบก่อนๆ                           -->
        <!-- ============================================================ -->
        <v-card outlined>
          <v-card-title class="text-subtitle-1 font-weight-bold pb-2">
            <v-icon left color="brown">mdi-history</v-icon>
            ประวัติการรายงานรอบก่อนหน้า
          </v-card-title>
          <v-divider></v-divider>
          <v-data-table
            :headers="historyHeaders"
            :items="reportHistory"
            :items-per-page="10"
            :loading="historyLoading"
            no-data-text="ยังไม่มีประวัติการรายงาน"
            dense
            class="elevation-0"
          >
            <template v-slot:item.round_info="{ item }">
              <div>
                <span class="font-weight-bold"
                  >ปี {{ item.cycle_fiscal_year }}</span
                >
                — {{ item.round_name }}
              </div>
            </template>
            <template v-slot:item.submission_status="{ item }">
              <v-chip x-small :color="statusColor(item.submission_status)" dark>
                <v-icon x-small left>{{
                  statusIcon(item.submission_status)
                }}</v-icon>
                {{ statusLabel(item.submission_status) }}
              </v-chip>
            </template>
            <template v-slot:item.submission_count_total="{ item }">
              <span class="font-weight-bold">{{
                item.submission_count_total || 0
              }}</span>
              คน
            </template>
            <template v-slot:item.submission_confirmed_at="{ item }">
              {{
                item.submission_confirmed_at
                  ? formatThaiDate(item.submission_confirmed_at)
                  : "-"
              }}
            </template>
            <template v-slot:item.actions="{ item }">
              <v-btn
                v-if="item.file_name"
                icon
                small
                color="deep-purple"
                @click="
                  openPdfPreview(
                    item.file_name,
                    'รายงาน PDF — ' + item.round_name
                  )
                "
              >
                <v-icon small>mdi-file-pdf-box</v-icon>
              </v-btn>
              <v-btn
                v-if="item.signed_pdf_path"
                icon
                small
                color="red"
                @click="
                  openPdfPreview(
                    item.signed_pdf_path,
                    'เอกสารลงนาม — ' + item.round_name
                  )
                "
              >
                <v-icon small>mdi-file-sign</v-icon>
              </v-btn>
              <v-btn
                icon
                small
                color="indigo"
                @click="viewSnapshot(item.submission_id)"
              >
                <v-icon small>mdi-eye</v-icon>
              </v-btn>
            </template>
          </v-data-table>
        </v-card>
      </base-material-card>

      <!-- ============================================================ -->
      <!-- Dialogs                                                      -->
      <!-- ============================================================ -->

      <!-- PDF Preview -->
      <v-dialog v-model="pdfPreviewDialog" max-width="900" scrollable>
        <v-card>
          <v-card-title class="text-subtitle-1 font-weight-bold py-2">
            <v-icon left color="deep-purple">mdi-file-pdf-box</v-icon>
            {{ pdfPreviewTitle }}
            <v-spacer></v-spacer>
            <v-btn icon small @click="pdfPreviewDialog = false"
              ><v-icon>mdi-close</v-icon></v-btn
            >
          </v-card-title>
          <v-divider></v-divider>
          <v-card-text class="pa-0" style="height: 75vh; overflow: hidden;">
            <iframe
              v-if="pdfPreviewUrl"
              ref="pdfIframe"
              :src="
                'https://hrvec.ovec.go.th/HRvec_api/personnel_reports/' +
                  pdfPreviewUrl
              "
              width="100%"
              height="100%"
              style="border: none;"
            ></iframe>
          </v-card-text>
        </v-card>
      </v-dialog>

      <!-- Snapshot Dialog -->
      <v-dialog v-model="snapshotDialog" persistent max-width="90%" scrollable>
        <v-card>
          <v-card-title class="text-subtitle-1 font-weight-bold">
            <v-icon left color="indigo">mdi-database-eye</v-icon>
            ข้อมูล Snapshot บุคลากร
            <v-spacer></v-spacer>
            <v-btn icon @click="snapshotDialog = false"
              ><v-icon>mdi-close</v-icon></v-btn
            >
          </v-card-title>
          <v-divider></v-divider>
          <v-card-text style="max-height: 600px;">
            <v-text-field
              v-model="dialogSnapshotSearch"
              append-icon="mdi-magnify"
              label="ค้นหา"
              single-line
              hide-details
              dense
              outlined
              class="mb-3"
            ></v-text-field>
            <v-data-table
              :headers="allSnapshotHeaders"
              :items="dialogSnapshotData"
              :search="dialogSnapshotSearch"
              :items-per-page="15"
              dense
            >
              <template v-slot:item.snapshot_m_type="{ item }">
                <v-chip x-small :color="typeColor(item.snapshot_m_type)" dark>{{
                  typeLabel(item.snapshot_m_type)
                }}</v-chip>
              </template>
              <template v-slot:item.fullname="{ item }">
                {{ item.snapshot_title }}{{ item.snapshot_fristname }}
                {{ item.snapshot_lastname }}
              </template>
              <template v-slot:item.snapshot_salary="{ item }">
                {{
                  item.snapshot_salary
                    ? Number(item.snapshot_salary).toLocaleString()
                    : "-"
                }}
              </template>
              <template v-slot:item.snapshot_teach_hours_s1="{ item }">
                <span v-if="isTeachingType(item.snapshot_m_type)">{{
                  item.snapshot_teach_hours_s1 || "-"
                }}</span>
                <span v-else>-</span>
              </template>
              <template v-slot:item.snapshot_teach_hours_s2="{ item }">
                <span v-if="isTeachingType(item.snapshot_m_type)">{{
                  item.snapshot_teach_hours_s2 || "-"
                }}</span>
                <span v-else>-</span>
              </template>
            </v-data-table>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="grey" text @click="snapshotDialog = false">ปิด</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Confirm Dialog -->
      <v-dialog v-model="confirmDialog" persistent max-width="500">
        <v-card class="pa-4">
          <v-card-title class="text-h6 font-weight-bold justify-center">
            <v-icon left :color="confirmDialogData.color" large>{{
              confirmDialogData.icon
            }}</v-icon>
            {{ confirmDialogData.title }}
          </v-card-title>
          <v-card-text class="text-center text-body-1 py-4">{{
            confirmDialogData.message
          }}</v-card-text>
          <v-card-actions class="justify-center">
            <v-btn outlined rounded @click="confirmDialog = false" class="mr-3">
              <v-icon left small>mdi-close</v-icon>ยกเลิก
            </v-btn>
            <v-btn
              :color="confirmDialogData.color"
              dark
              rounded
              depressed
              @click="confirmDialogData.action()"
            >
              <v-icon left small>{{ confirmDialogData.icon }}</v-icon
              >{{ confirmDialogData.btnText }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Snackbar -->
      <v-snackbar
        v-model="snackbar.show"
        :color="snackbar.color"
        :timeout="snackbar.timeout"
        top
        multi-line
      >
        <v-icon left>{{ snackbar.icon }}</v-icon
        >{{ snackbar.text }}
        <template v-slot:action="{ attrs }">
          <v-btn dark text v-bind="attrs" @click="snackbar.show = false"
            >ปิด</v-btn
          >
        </template>
      </v-snackbar>
    </v-container>
  </div>
</template>

<script>
export default {
  name: "PersonnelReport",

  data() {
    return {
      ApiKey: "HRvec2021",
      loading: false,
      historyLoading: false,
      confirmLoading: false,
      pdfLoading: false,
      certifyLoading: false,
      uploadLoading: false,
      resetLoading: false,

      user: {},
      collegeCode: "",
      activeSubmission: {},
      personnel_etcsCounts: {},
      snapshotData: [],
      snapshotSearch: "",
      reportHistory: [],
      snapshotDialog: false,
      dialogSnapshotData: [],
      dialogSnapshotSearch: "",
      signedPDFFile: null,
      generatedPDFPath: null,
      pdfPreviewDialog: false,
      pdfPreviewUrl: "",
      pdfPreviewTitle: "",
      confirmDialog: false,
      confirmDialogData: {
        title: "",
        message: "",
        icon: "",
        color: "",
        btnText: "",
        action: () => {}
      },
      snackbar: { show: false, color: "", icon: "", text: "", timeout: 4000 },

      // Base snapshot headers (ใช้สำหรับ snapshot ปัจจุบัน)
      baseSnapshotHeaders: [
        { text: "ลำดับ", value: "index", width: "60px", sortable: false },
        { text: "ประเภท", value: "snapshot_m_type", width: "160px" },
        { text: "ชื่อ-นามสกุล", value: "fullname", width: "220px" },
        {
          text: "เลขที่ตำแหน่ง",
          value: "snapshot_id_position",
          width: "120px"
        },
        { text: "อันดับ/ระดับ", value: "snapshot_level", width: "100px" },
        { text: "วิทยฐานะ", value: "snapshot_rang", width: "120px" },       
        { text: "ประเภทวิชา", value: "subject_name", width: "140px" },
        { text: "สาขาวิชา", value: "branch_name", width: "160px" }
      ],

      // All snapshot headers (ใช้สำหรับ dialog ดูประวัติ — แสดงทั้ง ภ.1 และ ภ.2)
      allSnapshotHeaders: [
        { text: "ลำดับ", value: "index", width: "50px", sortable: false },
        { text: "ประเภท", value: "snapshot_m_type", width: "140px" },
        { text: "ชื่อ-นามสกุล", value: "fullname", width: "200px" },
        {
          text: "เลขที่ตำแหน่ง",
          value: "snapshot_id_position",
          width: "100px"
        },
        { text: "อันดับ", value: "snapshot_level", width: "80px" },
        { text: "วิทยฐานะ", value: "snapshot_rang", width: "100px" },
        {
          text: "เงินเดือน",
          value: "snapshot_salary",
          width: "100px",
          align: "end"
        },
        { text: "ประเภทวิชา", value: "subject_name", width: "120px" },
        { text: "สาขาวิชา", value: "branch_name", width: "140px" },
        {
          text: "ชม.สอน ภ.1",
          value: "snapshot_teach_hours_s1",
          width: "100px",
          align: "center"
        },
        {
          text: "ชม.สอน ภ.2",
          value: "snapshot_teach_hours_s2",
          width: "100px",
          align: "center"
        }
      ],

      historyHeaders: [
        { text: "รอบรายงาน", value: "round_info", width: "280px" },
        { text: "สถานะ", value: "submission_status", width: "140px" },
        {
          text: "จำนวนบุคลากร",
          value: "submission_count_total",
          width: "130px",
          align: "center"
        },
        {
          text: "วันที่ยืนยัน",
          value: "submission_confirmed_at",
          width: "140px"
        },
        {
          text: "เครื่องมือ",
          value: "actions",
          width: "140px",
          sortable: false,
          align: "center"
        }
      ],

      typeMap: {
        director: { label: "ผู้อำนวยการ", color: "indigo" },
        se_director: { label: "รองผู้อำนวยการ", color: "blue" },
        teacher: { label: "ข้าราชการครู", color: "teal" },
        supervision: { label: "ศึกษานิเทศก์", color: "cyan" },
        "38": { label: "38 ค.(2)", color: "blue-grey" },
        permanent: { label: "ลูกจ้างประจำ", color: "brown" },
        governmentteach: { label: "พนง.ราชการ(สอน)", color: "orange darken-2" },
        governmentsp: { label: "พนง.ราชการ(สนับสนุน)", color: "deep-orange" },
        temporary: { label: "ครูอัตราจ้าง", color: "purple" },
        temporary_job: { label: "ลูกจ้างชั่วคราว", color: "pink" }
      },

      statusMap: {
        pending: {
          label: "รอดำเนินการ",
          color: "grey",
          icon: "mdi-clock-outline"
        },
        editing: { label: "กำลังแก้ไข", color: "orange", icon: "mdi-pencil" },
        confirmed: { label: "ยืนยันแล้ว", color: "blue", icon: "mdi-check" },
        pdf_generated: {
          label: "สร้าง PDF แล้ว",
          color: "deep-purple",
          icon: "mdi-file-pdf-box"
        },
        certified: {
          label: "ผอ. รับรองแล้ว",
          color: "amber darken-2",
          icon: "mdi-seal"
        },
        uploaded: {
          label: "อัปโหลดแล้ว",
          color: "red darken-1",
          icon: "mdi-cloud-check"
        },
        approved: { label: "อนุมัติ", color: "green", icon: "mdi-check-all" }
      },

      statusStepMap: {
        pending: 1,
        editing: 1,
        confirmed: 3,
        pdf_generated: 4,
        certified: 5,
        uploaded: 6,
        approved: 6
      }
    };
  },

  computed: {
    currentStep() {
      if (!this.activeSubmission || !this.activeSubmission.submission_status)
        return 1;
      return this.statusStepMap[this.activeSubmission.submission_status] || 1;
    },

    // ============================================================
    // ตรรกะรอบ → ภาคเรียน (เหมือนกับ personnel_etc)
    // round_number = 3 (พฤษภาคม) → ภาคเรียนที่ 1
    // round_number = 1 (ตุลาคม)  → ภาคเรียนที่ 2
    // ============================================================
    activeRoundTeachSemester() {
      if (!this.activeSubmission || !this.activeSubmission.round_number)
        return null;
      const rn = parseInt(this.activeSubmission.round_number);
      if (rn === 3) return 1;
      if (rn === 1) return 2;
      return null;
    },

    activeRoundTeachSemesterLabel() {
      const sem = this.activeRoundTeachSemester;
      if (sem === 1) return "ชั่วโมงสอน ภาคเรียนที่ 1";
      if (sem === 2) return "ชั่วโมงสอน ภาคเรียนที่ 2";
      return "";
    },

    // Snapshot headers แบบ dynamic — เพิ่มคอลัมน์ชั่วโมงสอนตามรอบ
    computedSnapshotHeaders() {
      let headers = [...this.baseSnapshotHeaders];
      const sem = this.activeRoundTeachSemester;
      if (sem === 1) {
        headers.push({
          text: "ชม.สอน ภ.1",
          value: "snapshot_teach_hours_s1",
          width: "110px",
          align: "center"
        });
      } else if (sem === 2) {
        headers.push({
          text: "ชม.สอน ภ.2",
          value: "snapshot_teach_hours_s2",
          width: "110px",
          align: "center"
        });
      } else {
        // ถ้าไม่ใช่รอบ 1 หรือ 3 แสดงทั้งสอง
        headers.push({
          text: "ชม.สอน ภ.1",
          value: "snapshot_teach_hours_s1",
          width: "100px",
          align: "center"
        });
        headers.push({
          text: "ชม.สอน ภ.2",
          value: "snapshot_teach_hours_s2",
          width: "100px",
          align: "center"
        });
      }
      return headers;
    },

    // สถิติชั่วโมงสอนตามรอบ (จากข้อมูล personnel_etc ปัจจุบัน ไม่ใช่ snapshot)
    teachHoursStats() {
      // ใช้ข้อมูลจาก snapshot ถ้ามี (หลังยืนยัน) หรือจะใช้ count จาก personnel_etc
      // ในที่นี้ใช้ snapshotData ถ้ามี มิฉะนั้นไม่แสดง
      const sem = this.activeRoundTeachSemester;
      if (!sem) return { totalTeachers: 0, withHours: 0, withoutHours: 0 };

      // ก่อนยืนยัน ยังไม่มี snapshot → return 0 (จะแสดง card แต่ไม่มีข้อมูล)
      // ใช้ personnel_etcsCounts สำหรับ totalTeachers
      const totalTeachers =
        parseInt(this.personnel_etcsCounts.teachers || 0) +
        parseInt(this.personnel_etcsCounts.governmentteach || 0) +
        parseInt(this.personnel_etcsCounts.temporarys || 0);

      // ไม่สามารถนับ withHours/withoutHours จาก counts ได้
      // ถ้า snapshot มี → ใช้ snapshot
      if (this.snapshotData.length > 0) {
        const field =
          sem === 1 ? "snapshot_teach_hours_s1" : "snapshot_teach_hours_s2";
        const teachers = this.snapshotData.filter(p =>
          this.isTeachingType(p.snapshot_m_type)
        );
        const withHours = teachers.filter(
          p => p[field] && parseInt(p[field]) > 0
        ).length;
        return {
          totalTeachers: teachers.length,
          withHours,
          withoutHours: teachers.length - withHours
        };
      }

      return { totalTeachers, withHours: 0, withoutHours: totalTeachers };
    },

    alertType() {
      const days = parseInt(this.activeSubmission.days_remaining);
      if (days <= 3) return "error";
      if (days <= 7) return "warning";
      return "info";
    },
    alertColor() {
      const days = parseInt(this.activeSubmission.days_remaining);
      if (days <= 3) return "error";
      if (days <= 7) return "warning";
      return "info";
    },
    daysRemainingColor() {
      const days = parseInt(this.activeSubmission.days_remaining);
      if (days <= 3) return "red";
      if (days <= 7) return "orange";
      return "green";
    },
    canConfirm() {
      return ["pending", "editing"].includes(
        this.activeSubmission.submission_status
      );
    },
    canGeneratePDF() {
      return this.activeSubmission.submission_status === "confirmed";
    },
    canCertify() {
      return this.activeSubmission.submission_status === "pdf_generated";
    },
    canUpload() {
      return this.activeSubmission.submission_status === "certified";
    },
    canReset() {
      const s = this.activeSubmission.submission_status;
      const days = parseInt(this.activeSubmission.days_remaining);
      return (
        ["uploaded", "confirmed", "pdf_generated", "certified"].includes(s) &&
        days >= 0
      );
    },
    isComplete() {
      return ["uploaded", "approved"].includes(
        this.activeSubmission.submission_status
      );
    },
    completionPercent() {
      const map = {
        pending: 0,
        editing: 10,
        confirmed: 35,
        pdf_generated: 55,
        certified: 75,
        uploaded: 100,
        approved: 100
      };
      return map[this.activeSubmission.submission_status] || 0;
    },
    personnelSummaryCards() {
      const c = this.personnel_etcsCounts;
      return [
        {
          label: "ผู้อำนวยการ",
          count: c.directors || 0,
          bg: "indigo lighten-5",
          textColor: "indigo--text"
        },
        {
          label: "รองผู้อำนวยการ",
          count: c.se_directors || 0,
          bg: "blue lighten-5",
          textColor: "blue--text text--darken-2"
        },
        {
          label: "ข้าราชการครู",
          count: c.teachers || 0,
          bg: "teal lighten-5",
          textColor: "teal--text text--darken-2"
        },
        {
          label: "ศึกษานิเทศก์",
          count: c.supervisions || 0,
          bg: "cyan lighten-5",
          textColor: "cyan--text text--darken-2"
        },
        {
          label: "38 ค.(2)",
          count: c.support_38 || 0,
          bg: "blue-grey lighten-5",
          textColor: "blue-grey--text"
        },
        {
          label: "ลูกจ้างประจำ",
          count: c.permanents || 0,
          bg: "brown lighten-5",
          textColor: "brown--text"
        },
        {
          label: "พนง.ราชการ(สอน)",
          count: c.governmentteach || 0,
          bg: "orange lighten-5",
          textColor: "orange--text text--darken-3"
        },
        {
          label: "พนง.ราชการ(สนับสนุน)",
          count: c.governmentsp || 0,
          bg: "deep-orange lighten-5",
          textColor: "deep-orange--text"
        },
        {
          label: "ครูอัตราจ้าง",
          count: c.temporarys || 0,
          bg: "purple lighten-5",
          textColor: "purple--text"
        },
        {
          label: "ลูกจ้างชั่วคราว",
          count: c.temporary_jobs || 0,
          bg: "pink lighten-5",
          textColor: "pink--text"
        }
      ];
    }
  },

  async mounted() {
    this.loading = true;
    try {
      let userSession = JSON.parse(sessionStorage.getItem("user")) || 0;
      let userResult = await this.$http.post("user.php", {
        ApiKey: this.ApiKey,
        user_ID: userSession.user_ID
      });
      this.user = userResult.data;
      this.collegeCode = userSession.user_name || this.user.user_code;

      await Promise.all([
        this.loadActiveSubmission(),
        this.loadPersonnelCounts(),
        this.loadHistory()
      ]);
    } catch (e) {
      console.error("Mount error:", e);
    } finally {
      this.loading = false;
    }
  },

  methods: {
    typeLabel(type) {
      return (this.typeMap[type] || {}).label || type || "ไม่ระบุ";
    },
    typeColor(type) {
      return (this.typeMap[type] || {}).color || "grey";
    },
    statusLabel(status) {
      return (this.statusMap[status] || {}).label || status || "-";
    },
    statusColor(status) {
      return (this.statusMap[status] || {}).color || "grey";
    },
    statusIcon(status) {
      return (this.statusMap[status] || {}).icon || "mdi-help-circle";
    },
    isStepComplete(step) {
      return this.currentStep > step;
    },
    stepColor(step) {
      if (this.currentStep > step) return "success";
      if (this.currentStep === step) return "primary";
      return "grey lighten-1";
    },

    isTeachingType(type) {
      return ["teacher", "governmentteach", "temporary"].includes(type);
    },

    formatThaiDate(dateStr) {
      if (!dateStr) return "-";
      const months = [
        "",
        "ม.ค.",
        "ก.พ.",
        "มี.ค.",
        "เม.ย.",
        "พ.ค.",
        "มิ.ย.",
        "ก.ค.",
        "ส.ค.",
        "ก.ย.",
        "ต.ค.",
        "พ.ย.",
        "ธ.ค."
      ];
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return dateStr;
      return `${d.getDate()} ${months[d.getMonth() + 1]} ${d.getFullYear() +
        543}`;
    },
    formatThaiDateTime(dateStr) {
      if (!dateStr) return "-";
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return dateStr;
      return `${this.formatThaiDate(dateStr)} ${String(d.getHours()).padStart(
        2,
        "0"
      )}:${String(d.getMinutes()).padStart(2, "0")} น.`;
    },
    showSnackbar(color, icon, text) {
      this.snackbar = { show: true, color, icon, text, timeout: 4000 };
    },

    async loadActiveSubmission() {
      try {
        let result = await this.$http.post("personnel_report_submission.php", {
          ApiKey: this.ApiKey,
          action: "get_my_submission",
          college_code: this.collegeCode
        });
        this.activeSubmission = result.data || {};
        if (
          this.activeSubmission.submission_id &&
          [
            "confirmed",
            "pdf_generated",
            "certified",
            "uploaded",
            "approved"
          ].includes(this.activeSubmission.submission_status)
        ) {
          this.loadSnapshot(this.activeSubmission.submission_id);
        }
        if (
          this.activeSubmission.submission_status &&
          ["pdf_generated", "certified", "uploaded", "approved"].includes(
            this.activeSubmission.submission_status
          )
        ) {
          this.loadGeneratedPDF();
        }
      } catch (e) {
        console.error("Load active submission error:", e);
        this.activeSubmission = {};
      }
    },

    async loadPersonnelCounts() {
      try {
        let userSession = JSON.parse(sessionStorage.getItem("user")) || {};
        let result = await this.$http.post("personnel_etc.php", {
          ApiKey: this.ApiKey,
          count_personnel: "Ok",
          personnel_etc_college_code: userSession.user_name
        });
        this.personnel_etcsCounts = result.data || {};
        let sum = Object.values(this.personnel_etcsCounts).reduce(
          (acc, val) => acc + (parseFloat(val) || 0),
          0
        );
        this.personnel_etcsCounts.sumAll = sum;
      } catch (e) {
        console.error("Load counts error:", e);
      }
    },

    async loadHistory() {
      this.historyLoading = true;
      try {
        let result = await this.$http.post("personnel_report_submission.php", {
          ApiKey: this.ApiKey,
          action: "get_history",
          college_code: this.collegeCode
        });
        this.reportHistory = Array.isArray(result.data) ? result.data : [];
      } catch (e) {
        this.reportHistory = [];
      } finally {
        this.historyLoading = false;
      }
    },

    async loadSnapshot(submissionId) {
      try {
        let result = await this.$http.post("personnel_report_submission.php", {
          ApiKey: this.ApiKey,
          action: "get_snapshot",
          submission_id: submissionId
        });
        let data = Array.isArray(result.data) ? result.data : [];
        data.forEach((item, idx) => {
          item.index = idx + 1;
        });
        this.snapshotData = data;
      } catch (e) {
        this.snapshotData = [];
      }
    },

    async loadGeneratedPDF() {
      if (this.reportHistory.length > 0) {
        const current = this.reportHistory.find(
          h => h.submission_id === this.activeSubmission.submission_id
        );
        if (current && current.file_name) {
          this.generatedPDFPath = current.file_name;
        }
      }
    },

    // Actions
    resetSubmission() {
      this.confirmDialogData = {
        title: "ยกเลิกและเริ่มรายงานใหม่",
        message:
          'ระบบจะลบ Snapshot และ PDF ที่สร้างไว้ แล้วรีเซ็ตสถานะกลับเป็น "รอดำเนินการ"',
        icon: "mdi-restart",
        color: "warning",
        btnText: "ยืนยันยกเลิก",
        action: this.doResetSubmission
      };
      this.confirmDialog = true;
    },
    async doResetSubmission() {
      this.confirmDialog = false;
      this.resetLoading = true;
      try {
        let result = await this.$http.post("personnel_report_submission.php", {
          ApiKey: this.ApiKey,
          action: "reset_submission",
          submission_id: this.activeSubmission.submission_id,
          college_code: this.collegeCode,
          user_account: this.collegeCode
        });
        if (result.data.status === true) {
          this.showSnackbar(
            "success",
            "mdi-check-circle",
            result.data.message || "ยกเลิกเรียบร้อย"
          );
          this.snapshotData = [];
          this.generatedPDFPath = null;
          await this.loadActiveSubmission();
          await this.loadPersonnelCounts();
          await this.loadHistory();
        } else {
          this.showSnackbar(
            "warning",
            "mdi-alert",
            result.data.message || "ไม่สามารถยกเลิกได้"
          );
        }
      } catch (e) {
        this.showSnackbar(
          "error",
          "mdi-alert-circle",
          "เกิดข้อผิดพลาด: " + e.message
        );
      } finally {
        this.resetLoading = false;
      }
    },

    confirmData() {
      const teachInfo = this.activeRoundTeachSemester
        ? ` (รวมชั่วโมงสอน ภ.${this.activeRoundTeachSemester})`
        : "";
      this.confirmDialogData = {
        title: "ยืนยันข้อมูลบุคลากร",
        message: `ระบบจะสร้าง Snapshot ข้อมูลบุคลากร ${this.personnel_etcsCounts
          .sumAll || 0} คน${teachInfo} ณ เวลานี้`,
        icon: "mdi-database-check",
        color: "teal",
        btnText: "ยืนยัน",
        action: this.doConfirmData
      };
      this.confirmDialog = true;
    },
    async doConfirmData() {
      this.confirmDialog = false;
      this.confirmLoading = true;
      try {
        let result = await this.$http.post("personnel_report_submission.php", {
          ApiKey: this.ApiKey,
          action: "confirm_data",
          submission_id: this.activeSubmission.submission_id,
          college_code: this.collegeCode,
          user_account: this.collegeCode
        });
        if (result.data.status === true) {
          this.showSnackbar("success", "mdi-check-circle", result.data.message);
          await this.loadActiveSubmission();
          await this.loadHistory();
        } else {
          this.showSnackbar("warning", "mdi-alert", result.data.message);
        }
      } catch (e) {
        this.showSnackbar(
          "error",
          "mdi-alert-circle",
          "เกิดข้อผิดพลาด: " + e.message
        );
      } finally {
        this.confirmLoading = false;
      }
    },

    generatePDF() {
      const teachInfo = this.activeRoundTeachSemester
        ? ` รวมคอลัมน์ชั่วโมงสอน ภ.${this.activeRoundTeachSemester}`
        : "";
      this.confirmDialogData = {
        title: "สร้างรายงาน PDF",
        message: `ระบบจะสร้างรายงาน PDF จากข้อมูล Snapshot${teachInfo}`,
        icon: "mdi-file-pdf-box",
        color: "deep-purple",
        btnText: "สร้าง PDF",
        action: this.doGeneratePDF
      };
      this.confirmDialog = true;
    },
    async doGeneratePDF() {
      this.confirmDialog = false;
      this.pdfLoading = true;
      try {
        let result = await this.$http.post("personnel_report_pdf.php", {
          ApiKey: this.ApiKey,
          submission_id: this.activeSubmission.submission_id,
          college_code: this.collegeCode,
          user_account: this.collegeCode
        });
        if (result.data.status === true) {
          this.generatedPDFPath = result.data.file_name;
          this.showSnackbar("success", "mdi-check-circle", result.data.message);
          await this.loadActiveSubmission();
          await this.loadHistory();
        } else {
          this.showSnackbar("warning", "mdi-alert", result.data.message);
        }
      } catch (e) {
        this.showSnackbar(
          "error",
          "mdi-alert-circle",
          "เกิดข้อผิดพลาด: " + e.message
        );
      } finally {
        this.pdfLoading = false;
      }
    },

    certifyData() {
      this.confirmDialogData = {
        title: "ยืนยันการรับรองจากผู้อำนวยการ",
        message: "ยืนยันว่าผู้อำนวยการได้ตรวจสอบและลงนามรับรองข้อมูลรายงานแล้ว",
        icon: "mdi-seal",
        color: "amber darken-2",
        btnText: "ยืนยันการรับรอง",
        action: this.doCertifyData
      };
      this.confirmDialog = true;
    },
    async doCertifyData() {
      this.confirmDialog = false;
      this.certifyLoading = true;
      try {
        let result = await this.$http.post("personnel_report_submission.php", {
          ApiKey: this.ApiKey,
          action: "certify",
          submission_id: this.activeSubmission.submission_id,
          user_account: this.collegeCode
        });
        if (result.data.status === true) {
          this.showSnackbar("success", "mdi-check-circle", result.data.message);
          await this.loadActiveSubmission();
          await this.loadHistory();
        } else {
          this.showSnackbar("warning", "mdi-alert", result.data.message);
        }
      } catch (e) {
        this.showSnackbar(
          "error",
          "mdi-alert-circle",
          "เกิดข้อผิดพลาด: " + e.message
        );
      } finally {
        this.certifyLoading = false;
      }
    },

    async uploadSignedPDF() {
      if (!this.signedPDFFile) return;
      if (this.signedPDFFile.size > 10 * 1024 * 1024) {
        this.showSnackbar("warning", "mdi-alert", "ขนาดไฟล์ต้องไม่เกิน 10MB");
        return;
      }
      this.uploadLoading = true;
      try {
        let formData = new FormData();
        formData.append("ApiKey", this.ApiKey);
        formData.append("file", this.signedPDFFile);
        formData.append("submission_id", this.activeSubmission.submission_id);
        formData.append("college_code", this.collegeCode);
        formData.append("file_type", "signed_pdf");
        formData.append("user_account", this.collegeCode);
        let result = await this.$http.post(
          "personnel_report_upload.php",
          formData,
          { headers: { "Content-Type": "multipart/form-data" } }
        );
        if (result.data.status === true) {
          this.showSnackbar("success", "mdi-check-circle", result.data.message);
          this.signedPDFFile = null;
          await this.loadActiveSubmission();
          await this.loadHistory();
        } else {
          this.showSnackbar("warning", "mdi-alert", result.data.message);
        }
      } catch (e) {
        this.showSnackbar(
          "error",
          "mdi-alert-circle",
          "เกิดข้อผิดพลาด: " + e.message
        );
      } finally {
        this.uploadLoading = false;
      }
    },

    openPdfPreview(url, title) {
      this.pdfPreviewUrl = url;
      this.pdfPreviewTitle = title || "แสดงตัวอย่าง PDF";
      this.pdfPreviewDialog = true;
    },

    async viewSnapshot(submissionId) {
      try {
        let result = await this.$http.post("personnel_report_submission.php", {
          ApiKey: this.ApiKey,
          action: "get_snapshot",
          submission_id: submissionId
        });
        let data = Array.isArray(result.data) ? result.data : [];
        data.forEach((item, idx) => {
          item.index = idx + 1;
        });
        this.dialogSnapshotData = data;
        this.dialogSnapshotSearch = "";
        this.snapshotDialog = true;
      } catch (e) {
        this.showSnackbar(
          "error",
          "mdi-alert-circle",
          "ไม่สามารถโหลดข้อมูลได้"
        );
      }
    }
  }
};
</script>

<style scoped>
.step-card {
  transition: all 0.3s ease;
  border-width: 1px;
}
.step-card:hover {
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
}
.border-active {
  border-color: #1976d2 !important;
  border-width: 2px !important;
  background: rgba(25, 118, 210, 0.02);
}
.border-success {
  border-color: #4caf50 !important;
  border-width: 2px !important;
  background: rgba(76, 175, 80, 0.03);
}
.v-stepper {
  box-shadow: none !important;
}
.v-stepper__header {
  box-shadow: none !important;
}
</style>
